import _merge from 'lodash/merge';

import {abilityFeature} from '../abilityFeature';

// Inline types for the runtime callbacks on options, decoupled from
// the narrowly-typed ExtendedAbilitySelectorOptions / ExtendedDiagnosticAbilitySelectorOptions
// in ../types. This lets us make recursiveSelectAbilityDiagnostics optional
// (it may be absent when called outside of diagnostic mode) and use plain
// strings instead of DesignerAbilitySelectorName (avoiding a coupling to the
// full selector name union which will cause import cycles once the builder
// API is added).
type RecursiveSelectAbility = (
  abilityName: string
) => (state: Record<string, unknown>, options?: unknown) => boolean;

type RecursiveSelectAbilityDiagnostics = (
  abilityName: string
) => (
  state: Record<string, unknown>,
  options: unknown
) => [boolean, unknown, unknown?];

type InternalAbilityOptions = {
  recursiveSelectAbility: RecursiveSelectAbility;
  recursiveSelectAbilityDiagnostics?: RecursiveSelectAbilityDiagnostics;
  [key: string]: unknown;
};

export const abilities = abilityFeature({
  key: 'abilities',
  isValid: function isAbilitiesValid(state, abilitiesToCheck, _options) {
    const options = _options as unknown as InternalAbilityOptions;
    return Object.entries(abilitiesToCheck).every(
      ([ability, expectation]: [string, boolean]) =>
        Boolean(
          options.recursiveSelectAbility(ability)(
            state as Record<string, unknown>,
            options
          )
        ) === expectation
    );
  },
  diagnose: function diagnoseAbilities(state, abilitiesToCheck, _options) {
    const options = _options as unknown as InternalAbilityOptions;
    let isValid = true;
    const expectations: Record<string, {expected: boolean; received: boolean}> =
      {};
    const diagnostics: Record<string, unknown> = {};

    for (const [ability, expectation] of Object.entries(abilitiesToCheck)) {
      const result = options.recursiveSelectAbilityDiagnostics?.(ability)(
        state as Record<string, unknown>,
        options
      );
      const [value, reasons] = result ?? [false, {}];
      _merge(diagnostics, reasons);

      if (value !== expectation) {
        isValid = false;
        expectations[ability] = {
          expected: expectation,
          received: Boolean(value),
        };
      }
    }

    return [isValid, expectations, diagnostics];
  },
});
